'use client'

import ConfirmModal from '@/components/confirm-modal'
import {
    BankAccountItem,
    CreateBankAccountData,
    TransferData,
    createBankAccount,
    deleteBankAccount,
    getBankAccounts,
    recalculateBalance,
    transferBetweenAccounts,
    updateBankAccount,
} from '@/lib/api/financial/bank-account-api'
import {
    ChartOfAccountsItem,
    getChartOfAccounts,
} from '@/lib/api/financial/financial-chart-of-accounts-api'
import { useResourcePermissions } from '@/lib/hooks/use-resource-permissions'
import { useAuth } from '@/store/hooks'
import { useEffect, useState } from 'react'
import {
    MdAccountBalance,
    MdAdd,
    MdClose,
    MdDelete,
    MdEdit,
    MdPix,
    MdReceipt,
    MdRefresh,
    MdSwapHoriz,
} from 'react-icons/md'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'

const formatCurrency = (cents: number) =>
    (cents / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })

interface AccountForm {
    name: string
    bankName: string
    bankCode: string
    agency: string
    accountNumber: string
    accountDigit: string
    accountType: 'CHECKING' | 'SAVINGS'
    pixKey: string
    initialBalance: string
    chartOfAccountsId: string
    active: boolean
}

const initialForm: AccountForm = {
    name: '',
    bankName: '',
    bankCode: '',
    agency: '',
    accountNumber: '',
    accountDigit: '',
    accountType: 'CHECKING',
    pixKey: '',
    initialBalance: '0',
    chartOfAccountsId: '',
    active: true,
}

interface TransferForm {
    toAccountId: string
    amount: string
    date: string
    description: string
}

const initialTransferForm: TransferForm = {
    toAccountId: '',
    amount: '',
    date: new Date().toISOString().split('T')[0],
    description: '',
}

export default function BankAccountsPage() {
    const navigate = useNavigate()
    const auth = useAuth()
    const { canCreate, canUpdate, canDelete } = useResourcePermissions('financial', 'bank_accounts')

    const [accounts, setAccounts] = useState<BankAccountItem[]>([])
    const [loading, setLoading] = useState(true)
    const [chartOfAccounts, setChartOfAccounts] = useState<ChartOfAccountsItem[]>([])

    // Create/Edit modal
    const [showFormModal, setShowFormModal] = useState(false)
    const [editingAccount, setEditingAccount] = useState<BankAccountItem | null>(null)
    const [form, setForm] = useState<AccountForm>(initialForm)
    const [saving, setSaving] = useState(false)

    // Transfer modal
    const [showTransferModal, setShowTransferModal] = useState(false)
    const [transferFrom, setTransferFrom] = useState<BankAccountItem | null>(null)
    const [transferForm, setTransferForm] = useState<TransferForm>(initialTransferForm)
    const [transferring, setTransferring] = useState(false)

    // Delete modal
    const [showDeleteModal, setShowDeleteModal] = useState(false)
    const [accountToDelete, setAccountToDelete] = useState<BankAccountItem | null>(null)
    const [deleting, setDeleting] = useState(false)

    useEffect(() => {
        loadAccounts()
        loadChartOfAccounts()
    }, [])

    const loadAccounts = async () => {
        setLoading(true)
        try {
            const response = await getBankAccounts()
            if (response.success) {
                setAccounts(response.result)
            } else {
                toast.error(response.error || 'Erro ao carregar contas bancárias')
            }
        } catch {
            toast.error('Erro ao carregar contas bancárias')
        } finally {
            setLoading(false)
        }
    }

    const loadChartOfAccounts = async () => {
        try {
            const response = await getChartOfAccounts({ type: 'ASSET' })
            if (response.success) {
                setChartOfAccounts(response.result)
            }
        } catch {
            // silent
        }
    }

    const openCreateModal = () => {
        setEditingAccount(null)
        setForm(initialForm)
        setShowFormModal(true)
    }

    const openEditModal = (account: BankAccountItem) => {
        setEditingAccount(account)
        setForm({
            name: account.name,
            bankName: account.bankName,
            bankCode: account.bankCode || '',
            agency: account.agency || '',
            accountNumber: account.accountNumber || '',
            accountDigit: (account as any).accountDigit || '',
            accountType: account.accountType,
            pixKey: account.pixKey || '',
            initialBalance: (account.initialBalance / 100).toString(),
            chartOfAccountsId: account.chartOfAccountsId?.toString() || '',
            active: account.active,
        })
        setShowFormModal(true)
    }

    const openTransferModal = (account: BankAccountItem) => {
        setTransferFrom(account)
        setTransferForm(initialTransferForm)
        setShowTransferModal(true)
    }

    const openDeleteModal = (account: BankAccountItem) => {
        setAccountToDelete(account)
        setShowDeleteModal(true)
    }

    const handleSave = async () => {
        if (!form.name.trim() || !form.bankName.trim()) {
            toast.error('Nome e Banco são obrigatórios')
            return
        }

        setSaving(true)
        try {
            const data: CreateBankAccountData = {
                name: form.name.trim(),
                bankName: form.bankName.trim(),
                bankCode: form.bankCode.trim() || undefined,
                agency: form.agency.trim() || undefined,
                accountNumber: form.accountNumber.trim() || undefined,
                accountDigit: form.accountDigit.trim() || undefined,
                accountType: form.accountType,
                pixKey: form.pixKey.trim() || undefined,
                initialBalance: Math.round(parseFloat(form.initialBalance || '0') * 100),
                chartOfAccountsId: form.chartOfAccountsId ? parseInt(form.chartOfAccountsId) : undefined,
            }

            if (editingAccount) {
                const response = await updateBankAccount(editingAccount.id, {
                    ...data,
                    active: form.active,
                })
                if (response.success) {
                    toast.success('Conta bancária atualizada com sucesso')
                    setShowFormModal(false)
                    loadAccounts()
                } else {
                    toast.error(response.error || 'Erro ao atualizar conta bancária')
                }
            } else {
                const response = await createBankAccount(data)
                if (response.success) {
                    toast.success('Conta bancária criada com sucesso')
                    setShowFormModal(false)
                    loadAccounts()
                } else {
                    toast.error(response.error || 'Erro ao criar conta bancária')
                }
            }
        } catch {
            toast.error('Erro ao salvar conta bancária')
        } finally {
            setSaving(false)
        }
    }

    const handleTransfer = async () => {
        if (!transferFrom || !transferForm.toAccountId || !transferForm.amount) {
            toast.error('Conta destino e valor são obrigatórios')
            return
        }

        const amountCents = Math.round(parseFloat(transferForm.amount) * 100)
        if (amountCents <= 0) {
            toast.error('O valor deve ser maior que zero')
            return
        }

        setTransferring(true)
        try {
            const data: TransferData = {
                toAccountId: parseInt(transferForm.toAccountId),
                amount: amountCents,
                date: transferForm.date,
                description: transferForm.description.trim() || undefined,
            }

            const response = await transferBetweenAccounts(transferFrom.id, data)
            if (response.success) {
                toast.success('Transferência realizada com sucesso')
                setShowTransferModal(false)
                loadAccounts()
            } else {
                toast.error(response.error || 'Erro ao realizar transferência')
            }
        } catch {
            toast.error('Erro ao realizar transferência')
        } finally {
            setTransferring(false)
        }
    }

    const handleDelete = async () => {
        if (!accountToDelete) return

        setDeleting(true)
        try {
            const response = await deleteBankAccount(accountToDelete.id)
            if (response.success) {
                toast.success('Conta bancária excluída com sucesso')
                setShowDeleteModal(false)
                setAccountToDelete(null)
                loadAccounts()
            } else {
                toast.error(response.error || 'Erro ao excluir conta bancária')
            }
        } catch {
            toast.error('Erro ao excluir conta bancária')
        } finally {
            setDeleting(false)
        }
    }

    const handleRecalculate = async (account: BankAccountItem) => {
        try {
            const response = await recalculateBalance(account.id)
            if (response.success) {
                toast.success('Saldo recalculado com sucesso')
                loadAccounts()
            } else {
                toast.error(response.error || 'Erro ao recalcular saldo')
            }
        } catch {
            toast.error('Erro ao recalcular saldo')
        }
    }

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                <div className="loading loading-spinner loading-lg text-secondary"></div>
                <p className="text-base-content/60 text-sm">Carregando contas bancárias...</p>
            </div>
        )
    }

    return (
        <div className="flex flex-col gap-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-secondary to-secondary/70 flex items-center justify-center shadow-lg">
                        <MdAccountBalance className="text-2xl text-white" />
                    </div>
                    <div>
                        <div className="flex items-center gap-2">
                            <h1 className="sm:text-lg lg:text-2xl font-bold text-base-content">
                                Contas Bancárias
                            </h1>
                            <span className="badge badge-secondary badge-sm">{accounts.length}</span>
                        </div>
                        <p className="text-sm text-base-content/60">
                            Gerencie suas contas bancárias
                        </p>
                    </div>
                </div>
                {canCreate && (
                    <button className="btn btn-secondary gap-2" onClick={openCreateModal}>
                        <MdAdd className="text-xl" />
                        <span className="hidden sm:inline">Nova Conta</span>
                    </button>
                )}
            </div>

            {/* Cards Grid or Empty State */}
            {accounts.length === 0 ? (
                <div className="flex flex-col items-center justify-center min-h-[400px] gap-4 bg-base-100 rounded-xl border border-base-300/50 p-8">
                    <div className="w-20 h-20 rounded-full bg-base-200 flex items-center justify-center">
                        <MdAccountBalance className="text-4xl text-base-content/30" />
                    </div>
                    <div className="text-center">
                        <h3 className="text-lg font-semibold text-base-content mb-1">
                            Nenhuma conta bancária cadastrada
                        </h3>
                        <p className="text-sm text-base-content/60 mb-4">
                            Comece adicionando sua primeira conta bancária
                        </p>
                        {canCreate && (
                            <button onClick={openCreateModal} className="btn btn-secondary gap-2">
                                <MdAdd className="text-xl" />
                                Adicionar primeira conta
                            </button>
                        )}
                    </div>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                    {accounts.map(account => (
                        <div
                            key={account.id}
                            className="bg-base-100 rounded-xl border border-base-300/50 p-5 hover:shadow-lg transition-all"
                        >
                            {/* Card Header */}
                            <div className="flex items-start justify-between mb-3">
                                <div className="flex-1 min-w-0">
                                    <h3 className="font-bold text-base truncate">{account.bankName}</h3>
                                    <p className="text-sm text-base-content/70 truncate">{account.name}</p>
                                </div>
                                <div className="flex items-center gap-1 ml-2">
                                    <span className={`badge badge-sm ${account.active ? 'badge-success' : 'badge-error'}`}>
                                        {account.active ? 'Ativa' : 'Inativa'}
                                    </span>
                                    <span className="badge badge-ghost badge-sm">
                                        {account.accountType === 'CHECKING' ? 'Corrente' : 'Poupança'}
                                    </span>
                                </div>
                            </div>

                            {/* Account Details */}
                            <div className="space-y-1 text-sm text-base-content/70 mb-3">
                                {(account.agency || account.accountNumber) && (
                                    <p>
                                        {account.agency && <>Ag: {account.agency}</>}
                                        {account.agency && account.accountNumber && ' / '}
                                        {account.accountNumber && <>Conta: {account.accountNumber}</>}
                                    </p>
                                )}
                                {account.bankCode && (
                                    <p>Banco: {account.bankCode}</p>
                                )}
                                {account.pixKey && (
                                    <p className="flex items-center gap-1">
                                        <MdPix className="text-base text-success" />
                                        <span className="truncate">{account.pixKey}</span>
                                    </p>
                                )}
                                {account.chartOfAccounts && (
                                    <p className="truncate">
                                        Plano: {account.chartOfAccounts.code} - {account.chartOfAccounts.title}
                                    </p>
                                )}
                            </div>

                            {/* Balance */}
                            <div className="bg-base-200/50 rounded-lg p-3 mb-3">
                                <p className="text-xs text-base-content/60 mb-1">Saldo Atual</p>
                                <p
                                    className={`text-lg font-bold ${account.currentBalance >= 0 ? 'text-success' : 'text-error'
                                        }`}
                                >
                                    {formatCurrency(account.currentBalance)}
                                </p>
                            </div>

                            {/* Actions */}
                            <div className="flex items-center justify-end gap-1 pt-2 border-t border-base-300/50">
                                {canUpdate && (
                                    <button
                                        onClick={() => openEditModal(account)}
                                        className="btn btn-ghost btn-sm"
                                        title="Editar"
                                    >
                                        <MdEdit className="text-lg" />
                                    </button>
                                )}
                                {canUpdate && (
                                    <button
                                        onClick={() => openTransferModal(account)}
                                        className="btn btn-ghost btn-sm"
                                        title="Transferência"
                                    >
                                        <MdSwapHoriz className="text-lg" />
                                    </button>
                                )}
                                <button
                                    onClick={() => navigate(`/financial/bank-accounts/${account.id}/extrato`)}
                                    className="btn btn-ghost btn-sm"
                                    title="Ver Extrato"
                                >
                                    <MdReceipt className="text-lg" />
                                </button>
                                <button
                                    onClick={() => handleRecalculate(account)}
                                    className="btn btn-ghost btn-sm"
                                    title="Recalcular Saldo"
                                >
                                    <MdRefresh className="text-lg" />
                                </button>
                                {canDelete && (
                                    <button
                                        onClick={() => openDeleteModal(account)}
                                        className="btn btn-ghost btn-sm text-error hover:bg-error/10"
                                        title="Excluir"
                                    >
                                        <MdDelete className="text-lg" />
                                    </button>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Create/Edit Modal */}
            <dialog className={`modal ${showFormModal ? 'modal-open' : ''}`}>
                <div className="modal-box w-11/12 !max-w-2xl">
                    <div className="flex items-center justify-between mb-6">
                        <h3 className="text-lg font-bold">
                            {editingAccount ? 'Editar Conta Bancária' : 'Nova Conta Bancária'}
                        </h3>
                        <button
                            onClick={() => setShowFormModal(false)}
                            className="btn btn-ghost btn-sm btn-circle"
                        >
                            <MdClose className="text-xl" />
                        </button>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="form-control">
                            <label className="label">
                                <span className="label-text">Nome *</span>
                            </label>
                            <input
                                type="text"
                                className="input input-bordered"
                                placeholder="Ex: Conta Principal"
                                value={form.name}
                                onChange={e => setForm({ ...form, name: e.target.value })}
                            />
                        </div>

                        <div className="form-control">
                            <label className="label">
                                <span className="label-text">Banco *</span>
                            </label>
                            <input
                                type="text"
                                className="input input-bordered"
                                placeholder="Ex: Banco do Brasil"
                                value={form.bankName}
                                onChange={e => setForm({ ...form, bankName: e.target.value })}
                            />
                        </div>

                        <div className="form-control">
                            <label className="label">
                                <span className="label-text">Código do Banco</span>
                            </label>
                            <input
                                type="text"
                                className="input input-bordered"
                                placeholder="Ex: 001"
                                value={form.bankCode}
                                onChange={e => setForm({ ...form, bankCode: e.target.value })}
                            />
                        </div>

                        <div className="form-control">
                            <label className="label">
                                <span className="label-text">Agência</span>
                            </label>
                            <input
                                type="text"
                                className="input input-bordered"
                                placeholder="Ex: 1234-5"
                                value={form.agency}
                                onChange={e => setForm({ ...form, agency: e.target.value })}
                            />
                        </div>

                        <div className="form-control">
                            <label className="label">
                                <span className="label-text">Número da Conta</span>
                            </label>
                            <input
                                type="text"
                                className="input input-bordered"
                                placeholder="Ex: 12345-6"
                                value={form.accountNumber}
                                onChange={e => setForm({ ...form, accountNumber: e.target.value })}
                            />
                        </div>

                        <div className="form-control">
                            <label className="label">
                                <span className="label-text">Dígito da Conta</span>
                            </label>
                            <input
                                type="text"
                                maxLength={2}
                                className="input input-bordered"
                                placeholder="Ex: 6"
                                value={form.accountDigit}
                                onChange={e => setForm({ ...form, accountDigit: e.target.value })}
                            />
                            <label className="label py-1">
                                <span className="label-text-alt text-base-content/50">
                                    Obrigatório para pagamento por TED
                                </span>
                            </label>
                        </div>

                        <div className="form-control">
                            <label className="label">
                                <span className="label-text">Tipo</span>
                            </label>
                            <select
                                className="select select-bordered"
                                value={form.accountType}
                                onChange={e =>
                                    setForm({ ...form, accountType: e.target.value as 'CHECKING' | 'SAVINGS' })
                                }
                            >
                                <option value="CHECKING">Corrente</option>
                                <option value="SAVINGS">Poupança</option>
                            </select>
                        </div>

                        <div className="form-control">
                            <label className="label">
                                <span className="label-text">Chave PIX</span>
                            </label>
                            <input
                                type="text"
                                className="input input-bordered"
                                placeholder="CPF, e-mail, telefone ou chave aleatória"
                                value={form.pixKey}
                                onChange={e => setForm({ ...form, pixKey: e.target.value })}
                            />
                        </div>

                        <div className="form-control">
                            <label className="label">
                                <span className="label-text">Saldo Inicial (R$)</span>
                            </label>
                            <input
                                type="number"
                                className="input input-bordered"
                                step="0.01"
                                placeholder="0,00"
                                value={form.initialBalance}
                                onChange={e => setForm({ ...form, initialBalance: e.target.value })}
                            />
                        </div>

                        <div className="form-control sm:col-span-2">
                            <label className="label">
                                <span className="label-text">Conta do Plano de Contas</span>
                            </label>
                            <select
                                className="select select-bordered"
                                value={form.chartOfAccountsId}
                                onChange={e =>
                                    setForm({ ...form, chartOfAccountsId: e.target.value })
                                }
                            >
                                <option value="">Nenhuma</option>
                                {chartOfAccounts.map(coa => (
                                    <option key={coa.id} value={coa.id}>
                                        {coa.code} - {coa.title}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {editingAccount && (
                            <div className="form-control sm:col-span-2">
                                <label className="label cursor-pointer justify-start gap-3">
                                    <input
                                        type="checkbox"
                                        className="toggle toggle-secondary"
                                        checked={form.active}
                                        onChange={e => setForm({ ...form, active: e.target.checked })}
                                    />
                                    <span className="label-text">Conta ativa</span>
                                </label>
                            </div>
                        )}
                    </div>

                    <div className="modal-action">
                        <button
                            className="btn btn-ghost"
                            onClick={() => setShowFormModal(false)}
                        >
                            Cancelar
                        </button>
                        <button
                            className="btn btn-secondary"
                            onClick={handleSave}
                            disabled={saving}
                        >
                            {saving && <span className="loading loading-spinner loading-sm"></span>}
                            {editingAccount ? 'Salvar' : 'Criar'}
                        </button>
                    </div>
                </div>
                <form method="dialog" className="modal-backdrop">
                    <button onClick={() => setShowFormModal(false)}>close</button>
                </form>
            </dialog>

            {/* Transfer Modal */}
            <dialog className={`modal ${showTransferModal ? 'modal-open' : ''}`}>
                <div className="modal-box w-11/12 max-w-lg">
                    <div className="flex items-center justify-between mb-6">
                        <h3 className="text-lg font-bold">
                            Transferência entre Contas
                        </h3>
                        <button
                            onClick={() => setShowTransferModal(false)}
                            className="btn btn-ghost btn-sm btn-circle"
                        >
                            <MdClose className="text-xl" />
                        </button>
                    </div>

                    {transferFrom && (
                        <div className="bg-base-200/50 rounded-lg p-3 mb-4">
                            <p className="text-xs text-base-content/60">Conta de origem</p>
                            <p className="font-semibold">{transferFrom.bankName} - {transferFrom.name}</p>
                            <p className="text-sm text-base-content/70">
                                Saldo: {formatCurrency(transferFrom.currentBalance)}
                            </p>
                        </div>
                    )}

                    <div className="space-y-4">
                        <div className="form-control">
                            <label className="label">
                                <span className="label-text">Conta Destino *</span>
                            </label>
                            <select
                                className="select select-bordered"
                                value={transferForm.toAccountId}
                                onChange={e =>
                                    setTransferForm({ ...transferForm, toAccountId: e.target.value })
                                }
                            >
                                <option value="">Selecione uma conta</option>
                                {accounts
                                    .filter(a => a.id !== transferFrom?.id && a.active)
                                    .map(a => (
                                        <option key={a.id} value={a.id}>
                                            {a.bankName} - {a.name}
                                        </option>
                                    ))}
                            </select>
                        </div>

                        <div className="form-control">
                            <label className="label">
                                <span className="label-text">Valor (R$) *</span>
                            </label>
                            <input
                                type="number"
                                className="input input-bordered"
                                step="0.01"
                                min="0.01"
                                placeholder="0,00"
                                value={transferForm.amount}
                                onChange={e =>
                                    setTransferForm({ ...transferForm, amount: e.target.value })
                                }
                            />
                        </div>

                        <div className="form-control">
                            <label className="label">
                                <span className="label-text">Data *</span>
                            </label>
                            <input
                                type="date"
                                className="input input-bordered"
                                value={transferForm.date}
                                onChange={e =>
                                    setTransferForm({ ...transferForm, date: e.target.value })
                                }
                            />
                        </div>

                        <div className="form-control">
                            <label className="label">
                                <span className="label-text">Descrição</span>
                            </label>
                            <input
                                type="text"
                                className="input input-bordered"
                                placeholder="Ex: Transferência entre contas"
                                value={transferForm.description}
                                onChange={e =>
                                    setTransferForm({ ...transferForm, description: e.target.value })
                                }
                            />
                        </div>
                    </div>

                    <div className="modal-action">
                        <button
                            className="btn btn-ghost"
                            onClick={() => setShowTransferModal(false)}
                        >
                            Cancelar
                        </button>
                        <button
                            className="btn btn-secondary"
                            onClick={handleTransfer}
                            disabled={transferring}
                        >
                            {transferring && <span className="loading loading-spinner loading-sm"></span>}
                            Transferir
                        </button>
                    </div>
                </div>
                <form method="dialog" className="modal-backdrop">
                    <button onClick={() => setShowTransferModal(false)}>close</button>
                </form>
            </dialog>

            {/* Delete Modal */}
            <ConfirmModal
                isOpen={showDeleteModal}
                onClose={() => {
                    setShowDeleteModal(false)
                    setAccountToDelete(null)
                }}
                onConfirm={handleDelete}
                title="Excluir conta bancária"
                message={`Tem certeza de que deseja excluir a conta "${accountToDelete?.name}"? Esta ação não pode ser desfeita.`}
                confirmButtonText="Excluir"
                cancelButtonText="Cancelar"
                loading={deleting}
            />
        </div>
    )
}
